import React, { useEffect, useState } from "react";
import { X, CheckCircle, Loader2 } from "lucide-react";
import { apiFetch } from "../api";
import { fetchPlanFileBlob, openPlanDownload, requestPlanDownloadPath } from "../utils/planDownload";

export default function PaymentSuccessModal({ onClose, onContinue, planUuid, planStatus }) {
  const [downloading, setDownloading] = useState(null);
  const [currentStatus, setCurrentStatus] = useState(planStatus || null);

  useEffect(() => {
    if (planStatus) {
      setCurrentStatus(planStatus);
    }
  }, [planStatus]);

  useEffect(() => {
    if (!planUuid) return undefined;
    if (currentStatus && currentStatus !== "PROCESSING") return undefined;
    let active = true;
    const fetchStatus = async () => {
      try {
        const res = await apiFetch(`/plan/by-uuid/${planUuid}`, { planUuid, silent: true });
        if (!res.ok) return;
        const data = await res.json();
        if (active && data?.status) {
          setCurrentStatus(data.status);
        }
      } catch (_) {}
    };
    fetchStatus();
    if (typeof window === "undefined") return undefined;
    const interval = window.setInterval(fetchStatus, 5000);
    return () => {
      active = false;
      window.clearInterval(interval);
    };
  }, [planUuid, currentStatus]);

  const download = async (type) => {
    if (currentStatus === "PROCESSING") return;
    setDownloading(type);
    try {
      const path = await requestPlanDownloadPath(planUuid, type);
      if (path) {
        openPlanDownload(path);
        return;
      }
      const blob = await fetchPlanFileBlob(planUuid, type);
      if (!blob) return;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `plan_${planUuid}.${type}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } finally {
      setDownloading(null);
    }
  };
  const isGenerating = currentStatus === "PROCESSING";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal>
      <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl text-center relative">
        <button onClick={onClose} className="absolute top-4 right-4 text-slate-400 hover:text-slate-600">
          <X className="h-5 w-5" />
        </button>
        <div className="flex justify-center mb-4">
          <CheckCircle className="h-12 w-12 text-green-500" />
        </div>
        <h2 className="text-xl font-semibold mb-4">Оплата прошла успешно</h2>
        {isGenerating && (
          <p className="mb-4 text-sm text-slate-600">
            Генерация еще в процессе. Для скачивания файлов дождитесь завершения
          </p>
        )}
        <div className="space-y-3">
          <button
            onClick={() => download("pdf")}
            disabled={isGenerating || downloading === 'pdf'}
            className="w-full h-11 rounded-lg bg-blue-600 text-white font-semibold shadow hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {isGenerating ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" /> Скачать PDF
              </>
            ) : downloading === 'pdf' ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              'Скачать PDF'
            )}
          </button>
          <button
            onClick={() => download("docx")}
            disabled={isGenerating || downloading === 'docx'}
            className="w-full h-11 rounded-lg border disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {isGenerating ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" /> Скачать DOCX (Word)
              </>
            ) : downloading === 'docx' ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              'Скачать DOCX (Word)'
            )}
          </button>
          <button
            onClick={onContinue || onClose}
            className="w-full h-11 rounded-lg border"
          >
            Продолжить редактирование
          </button>
        </div>
      </div>
    </div>
  );
}
